/* eslint-disable camelcase */
/* eslint-disable no-useless-catch */
/* eslint-disable no-plusplus */
/* eslint-disable no-await-in-loop */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useStatus, SUCCESS, ERROR } from '@rootstrap/redux-tools';

import { useAnalytics, useNavigation, useToast } from 'hooks';
import capitalize from 'lodash/capitalize';
import { addSeries, getTrailerUrlForJWPlayer } from 'state/actions/seriesActions';
import { parseHashtags, routeWithProps, log } from 'utils/helpers';
import { clearEpisodes } from 'state/actions/episodesActions';
import { routesPaths } from 'constants/routesPaths';
import parseError from 'utils/parseError';
import UserService from 'services/userService';
import { MIXPANEL_EVENTS, MIXPANEL_PARAMS } from 'constants/constants';
import axios from 'axios';
import SeriesService from 'services/seriesService';
import useFileUpload from 'hooks/common/useFileUpload';

export default () => {
  const { trackEvent } = useAnalytics();
  const dispatch = useDispatch();
  const { goTo } = useNavigation();
  const username = useRef();
  const [loading, setLoading] = useState(false);
  const { showErrorToast, showToast } = useToast();
  const [series, setSeries] = useState({});
  const [seriesId, setSeriesId] = useState(null);
  const { status, error } = useStatus(addSeries);
  const { trailerData } = useSelector(({ seriesUpload: { trailerData } }) => ({ trailerData }));
  const { episodes } = useSelector(({ episodes: { episodes } }) => ({ episodes }));
  const [completedVideos, setCompletedVideos] = useState(0);
  const [uploadedVideos, setUploadedVideos] = useState(0);
  const [hasVideosToUpload, setHasVideosToUpload] = useState(false);
  const totalVideoRef = useRef();
  const videoDoneRef = useRef();
  const totalVideos = episodes.length + 1;
  const { status: getTrailerUrlStatus, error: getTrailerUrlError } = useStatus(
    getTrailerUrlForJWPlayer
  );

  // Initialize file upload hook
  const {
    uploadFile,
    uploadProgress,
    uploadStage,
    currentFileName,
    setUploadStage,
    setUploadProgress,
    setCurrentFileName
  } = useFileUpload({
    onUploadProgress: progress => {
      setUploadProgress(progress);
    },
    onUploadComplete: () => {
      setUploadStage('processing');
    },
    onUploadError: error => {
      log('error', 'Error uploading file', {}, error);
      setUploadStage('');
    },
    getMultipartUploadUrl: (uploadId, mediaId, partNumber) =>
      SeriesService.getMultipartUploadUrl(uploadId, mediaId, partNumber),
    completeMultipartUpload: (uploadId, mediaId, parts) =>
      SeriesService.completeMultipartUpload(uploadId, mediaId, parts),
    onRedirect: id => navigateToEditSeries(id),
    redirectId: seriesId
  });

  const addNewSeries = async (seriesData = null) => {
    // Use the passed seriesData if provided, otherwise use the state
    const currentData = seriesData || series;

    // Ensure we have a valid series object with necessary properties
    if (!currentData || !currentData.title) {
      log('error', 'Invalid series data', {
        series: currentData,
        hasTitle: currentData?.title,
        seriesType: typeof currentData,
        fromState: !seriesData
      });
      setLoading(false);
      return;
    }

    const { categoryIds = [], description, hashtags = [], title, trailer } = currentData;

    // Validate trailer
    if (!trailer) {
      log('error', 'Missing trailer data', { trailer });
      showErrorToast('Error: Trailer data is missing. Please upload a trailer.');
      setLoading(false);
      return;
    }

    const data = {
      description,
      title,
      banner_media_id: trailer?.isMultipart ? trailer?.mediaId : trailerData?.mediaId,
      mime_type: trailer?.type
    };

    // Verify banner_media_id is present
    if (!data.banner_media_id) {
      log('error', 'Missing banner_media_id', {
        isMultipart: !!trailer?.isMultipart,
        trailerMediaId: trailer?.mediaId,
        trailerDataMediaId: trailerData?.mediaId
      });
      showErrorToast('Error: Failed to get media ID for trailer. Please try again.');
      setLoading(false);
      return;
    }

    // Ensure episodes exist and is an array
    if (!episodes || !Array.isArray(episodes)) {
      log('error', 'No episodes found or episodes is not an array', {
        hasEpisodes: !!episodes,
        isArray: Array.isArray(episodes)
      });
      showErrorToast('Error: No episodes available. Please add at least one episode.');
      setLoading(false);
      return;
    }

    const episodesList = episodes.map(
      (
        {
          creditStartTime,
          description,
          earlyReleaseDateTime,
          exclusive,
          mediaId,
          membersPrice,
          nonMembersPrice,
          releaseDateTime,
          title,
          seriesVideo,
          content_viewer_category,
          lead_cast,
          credits,
          notify_subscribers,
          duration
        },
        index
      ) => ({
        credits_start_time: creditStartTime,
        early_release_date_time: earlyReleaseDateTime,
        media_id: seriesVideo?.isMultipart ? seriesVideo?.mediaId : mediaId,
        members_price: membersPrice === 0 ? null : Number(membersPrice).toFixed(2),
        non_members_price: nonMembersPrice === 0 ? null : Number(nonMembersPrice).toFixed(2),
        release_date_time: releaseDateTime,
        position: index,
        mime_type: seriesVideo.type,
        description,
        exclusive,
        title,
        lead_cast,
        credits,
        content_viewer_category,
        notify_subscribers,
        duration: duration || '00:00:00'
      })
    );

    log('info', 'Creating FormData for series upload', {
      categoryCount: categoryIds?.length || 0,
      hashtagCount: hashtags?.length || 0,
      episodesCount: episodesList?.length || 0
    });

    const formData = new FormData();
    Object.keys(data).forEach(key => {
      formData.append(`series[${key}]`, data[key]);
    });

    formData.append('series[logo_image]', currentData.logo_image);
    formData.append('series[desktop_image]', currentData.desktop_image);
    formData.append('series[mobile_image]', currentData.mobile_image);

    formData.append('series[category_ids]', JSON.stringify(categoryIds || []));
    formData.append('series[hashtags]', JSON.stringify(hashtags || []));
    formData.append('series[episodes]', JSON.stringify(episodesList || []));

    formData.append('series[lead_cast]', currentData.lead_cast || '');
    formData.append('series[content_viewer_category]', currentData.content_viewer_category || '');
    formData.append('series[notify_subscribers]', currentData.notify_subscribers || false);
    formData.append('series[credits]', currentData.credits || '');
    // formData.append('series[season]', series.season);

    log('info', 'Dispatching addSeries action');
    const response = await dispatch(addSeries(formData));
    if (response?.payload?.data?.series?.id) {
      setSeriesId(response.payload.data.series.id);
    }
  };

  useEffect(() => {
    if (getTrailerUrlStatus === SUCCESS) {
      log('info', 'Successfully obtained trailer URL', { trailerData });
      const currentSeriesData = { ...series };
      addNewSeries(currentSeriesData);
      return;
    }

    if (getTrailerUrlStatus === ERROR) {
      log('error', 'Error getting trailer URL', { error: getTrailerUrlError });
      showErrorToast(getTrailerUrlError);
      dispatch(getTrailerUrlForJWPlayer.reset());
      setLoading(false);
    }
  }, [getTrailerUrlStatus]);

  // Validation function to be called before upload
  const validateBeforeUpload = async data => {
    log('info', 'Validating series data before upload', {
      hasTrailer: !!data.trailer,
      hasLogo: !!data.logo_image,
      hasDesktopImage: !!data.desktop_image,
      hasMobileImage: !!data.mobile_image,
      categoryCount: data.categoryIds?.length
    });

    setUploadStage('validating');

    // Perform validation checks
    if (!data.trailer || !data.logo_image || !data.desktop_image || !data.mobile_image) {
      log('error', 'Missing required media files', {
        hasTrailer: !!data.trailer,
        hasLogo: !!data.logo_image,
        hasDesktopImage: !!data.desktop_image,
        hasMobileImage: !!data.mobile_image
      });
      showErrorToast('Please upload all required media files');
      setUploadStage('');
      return false;
    }

    // Improved category validation with more detailed logging
    if (!data.categoryIds || !Array.isArray(data.categoryIds) || data.categoryIds.length === 0) {
      showErrorToast('Please select at least one category');
      setUploadStage('');
      return false;
    }

    log('info', 'Validation passed');
    setUploadStage('validated');
    return true;
  };

  const onSubmit = useCallback(
    async data => {
      log('info', 'Starting series submission', {
        title: data.title,
        hasCategoryIds: !!data.categoryIds,
        categoryIdsType: typeof data.categoryIds,
        hasTrailer: !!data.trailer,
        trailerType: typeof data.trailer,
        hasLogo: !!data.logo_image,
        hasDesktopImage: !!data.desktop_image,
        hasMobileImage: !!data.mobile_image,
        hasEpisodes: Array.isArray(data.episodes) && data.episodes.length > 0
      });

      setLoading(true);
      setHasVideosToUpload(true);
      setUploadStage('validating');

      const categoryIds = (data.categoryIds || [])
        .filter(item => item && typeof item === 'object' && 'value' in item)
        .map(item => item.value);

      log('info', 'Processed category IDs', {
        categoryCount: categoryIds.length,
        categoryIds
      });

      data.hashtags = parseHashtags(data.description);
      data.categoryIds = categoryIds;

      const isValid = await validateBeforeUpload(data);
      if (!isValid) {
        setLoading(false);
        setUploadStage('');
        return;
      }

      log('info', 'Setting series data and getting trailer URL', {
        title: data.title,
        hasTitle: !!data.title,
        hasTrailer: !!data.trailer,
        isMultipart: !!data.trailer?.isMultipart
      });

      setSeries(data);

      if (data.trailer) {
        if (typeof data.trailer === 'string') {
          setCurrentFileName(data.trailer);
        } else if (data.trailer.originalFile) {
          setCurrentFileName(data.trailer.originalFile.name);
        } else {
          setCurrentFileName(data.trailer.name);
        }
      }

      if (data.trailer?.isMultipart) {
        log('info', 'Using existing multipart upload for trailer', {
          uploadId: data.trailer.uploadId,
          mediaId: data.trailer.mediaId
        });
        addNewSeries(data);
      } else {
        dispatch(getTrailerUrlForJWPlayer(data.trailer));
      }
    },
    [dispatch, showErrorToast]
  );

  const resetDispatchData = () => {
    log('info', 'Resetting dispatch data');
    dispatch(clearEpisodes());
    dispatch(addSeries.reset());
    dispatch(getTrailerUrlForJWPlayer.reset());
    setLoading(false);
    setUploadStage('');
    setHasVideosToUpload(false);
  };

  const onCompleteVideoUpload = () => {
    log('info', 'Video upload process completed');
    resetDispatchData();
    showToast('Publication successfully uploaded!');
    setLoading(false);
    setHasVideosToUpload(false);

    log('info', 'Navigating to celebrity profile');
    setTimeout(() => {
      goTo(
        routeWithProps(routesPaths.celebrityId, {
          username: username.current.toLowerCase()
        })
      );
    }, 2000); // Added 2 second delay before redirect
  };

  const getProfile = async () => {
    try {
      log('info', 'Fetching user profile');
      const { data } = await UserService.getProfile();
      username.current = data.user.username;
      log('info', 'Profile fetched successfully', { username: data.user.username });
    } catch (error) {
      log('error', 'Error fetching profile', {}, error);
      throw parseError(error.response);
    }
  };

  useEffect(() => {
    getProfile();
  }, []);

  useEffect(() => {
    if (completedVideos === totalVideos) {
      log('info', 'All videos completed upload', {
        completedVideos,
        totalVideos
      });
      // Show follow-up message after 20 seconds
      showToast('Processing your data. Please wait for few mins...');

      // Complete the upload after 30 seconds
      setTimeout(() => {
        onCompleteVideoUpload();
        videoDoneRef.current = 2;
      }, 5000);
    }
  }, [completedVideos]);

  // Add a function to handle navigation to edit series page
  const navigateToEditSeries = id => {
    if (id) {
      // Get the celebrityId from the username reference if available
      const celebrityId = username.current ? username.current.toLowerCase() : '';
      log(
        'info',
        `Refreshing page and redirecting to edit originals for series ID: ${id}, celebrity ID: ${celebrityId}`
      );

      // Show a toast message before redirecting
      showErrorToast(
        'Upload process interrupted. Redirecting to edit page where you can try again...'
      );

      // Add a short delay to ensure the toast is visible before redirect
      setTimeout(() => {
        // Use window.location.href to force a full page refresh instead of React navigation
        window.location.href = `/edit-originals/${id}/${celebrityId}`;
      }, 1500);
    } else {
      log('error', 'Cannot redirect to edit originals page: No series ID available');
      showErrorToast('Error: Could not find series ID for redirect.');
    }
  };

  const uploadVideosInJWPlayer = async videos => {
    try {
      log('info', 'Starting batch upload to JWPlayer', { videoCount: videos.length });
      videoDoneRef.current = 1;
      setUploadProgress(0);
      setUploadedVideos(0);
      totalVideoRef.current = videos.length;
      setUploadStage('validated');
      setHasVideosToUpload(true);

      for (let index = 0; index < videos.length; index++) {
        const video = videos[index];
        setUploadedVideos(index);

        log('info', `Starting upload for video ${index + 1} of ${videos.length}`, {
          videoIndex: index,
          totalVideos: videos.length
        });

        if (video.video) {
          if (typeof video.video === 'string') {
            setCurrentFileName(video.video);
          } else if (video.video.originalFile) {
            setCurrentFileName(video.video.originalFile.name);
          } else {
            setCurrentFileName(video.video.name);
          }
        }

        let uploadSuccess = true;
        if (video.video.isMultipart) {
          const { uploadId, mediaId, originalFile } = video.video;
          uploadSuccess = await uploadFile(null, originalFile, uploadId, mediaId);
        } else {
          const fileToUpload = video.video;
          uploadSuccess = await uploadFile(video.uploadUrl, fileToUpload);
        }

        if (uploadSuccess === false) {
          log('info', 'Upload failed, stopping batch process for redirection');
          return;
        }

        setCompletedVideos(prev => prev + 1);
        setUploadProgress(0);
        log('info', `Completed video ${index + 1} of ${videos.length}`);
      }

      log('info', 'All videos uploaded successfully');
      setUploadStage('completed');
    } catch (error) {
      log('error', 'Error in batch upload process', {}, error);
      showErrorToast('Something went wrong, try again please!');
      setUploadStage('');

      if (seriesId) {
        log('info', `Batch upload failed. Redirecting to edit series page: ${seriesId}`);
        navigateToEditSeries(seriesId);
      }
    }
  };

  useEffect(() => {
    if (status === SUCCESS) {
      log('info', 'Series created successfully, preparing to upload videos');
      setLoading(true);
      setHasVideosToUpload(true);
      trackEvent(MIXPANEL_EVENTS.uploadSeries, {
        [MIXPANEL_PARAMS.seriesName]: series.title
      });

      const videosToUpload = [
        {
          video: series.trailer,
          mediaId: series.trailer?.isMultipart ? series.trailer?.mediaId : trailerData?.mediaId,
          uploadUrl: trailerData?.uploadUrl
        }
      ];

      episodes.forEach(({ mediaId, uploadUrl, seriesVideo }) => {
        videosToUpload.push({
          video: seriesVideo,
          mediaId,
          uploadUrl
        });
      });

      log('info', 'Starting upload process', {
        videoCount: videosToUpload.length,
        trailerSize: series.trailer.size,
        hasSeries: videosToUpload.length > 1,
        trailerIsMultipart: !!series.trailer?.isMultipart
      });

      uploadVideosInJWPlayer(videosToUpload);
    } else if (status === ERROR) {
      log('error', 'Error creating series', { error });
      showErrorToast(error);
    }
  }, [status, dispatch]);

  return {
    onSubmit,
    status,
    loading,
    uploadProgress,
    uploadedVideos,
    error: capitalize(error),
    totalVideos: totalVideoRef.current,
    uploadingStatus: videoDoneRef.current,
    uploadStage,
    seriesId,
    currentFileName,
    hasVideosToUpload
  };
};
